// ====================================================
// src/components/block/liveStreamManagement/videoEffects/PersonSegmenter.ts
//
// Détourage temps réel de la personne filmée, pour l'effet "Arrière-plan" (modes
// flou/image, cf. SurroundingsEffect.tsx). Basé sur MediaPipe
// Tasks Vision (WASM) — modèle "selfie segmenter" hébergé par Google, chargé à
// la demande, une seule fois pour toute la session (singleton).
//
// La source segmentée est toujours une <video> DOM, caméra ou HLS : le pipeline
// ne sait rien de l'origine du flux, il ne fait que lire des pixels.
// ====================================================

import { FilesetResolver, ImageSegmenter, type ImageSegmenterResult } from '@mediapipe/tasks-vision';

const WASM_BASE = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm';
const MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite';

let segmenterPromise: Promise<ImageSegmenter> | null = null;

async function createSegmenter(vision: Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>>, delegate: 'GPU' | 'CPU'): Promise<ImageSegmenter> {
  return ImageSegmenter.createFromOptions(vision, {
    baseOptions: { modelAssetPath: MODEL_URL, delegate },
    runningMode: 'VIDEO',
    outputCategoryMask: false,
    outputConfidenceMasks: true,
  });
}

function getSegmenter(): Promise<ImageSegmenter> {
  if (!segmenterPromise) {
    segmenterPromise = FilesetResolver.forVisionTasks(WASM_BASE).then(async vision => {
      try {
        return await createSegmenter(vision, 'GPU');
      } catch (err) {
        console.warn('[PersonSegmenter] délégué GPU indisponible, repli CPU :', err);
        return createSegmenter(vision, 'CPU');
      }
    });
  }
  return segmenterPromise;
}

export interface PersonMask {
  /** Confiance "personne" par pixel, 0..1, à la résolution du modèle (pas celle de la vidéo). */
  data: Float32Array;
  width: number;
  height: number;
}

/**
 * Segmente une frame de `video`. `timestampMs` doit être strictement croissant entre deux
 * appels — un seul appelant à la fois doit s'en servir (cf. son usage dans SourceConfigModal,
 * une seule préview segmentée ouverte à la fois : un compositeur qui se chevaucherait avec
 * un autre casserait cette contrainte).
 */
export async function segmentPersonInVideo(video: HTMLVideoElement, timestampMs: number): Promise<PersonMask | null> {
  if (video.readyState < 2 || video.videoWidth === 0) return null;
  let segmenter: ImageSegmenter;
  try {
    segmenter = await getSegmenter();
  } catch (err) {
    console.warn('[PersonSegmenter] échec de chargement du modèle :', err);
    return null;
  }

  return new Promise<PersonMask | null>(resolve => {
    try {
      segmenter.segmentForVideo(video, timestampMs, (result: ImageSegmenterResult) => {
        const mask = result.confidenceMasks?.[0];
        if (!mask) { resolve(null); return; }
        const data   = mask.getAsFloat32Array();
        const width  = mask.width;
        const height = mask.height;
        result.confidenceMasks?.forEach(m => m.close());
        resolve({ data, width, height });
      });
    } catch (err) {
      console.warn('[PersonSegmenter] échec de segmentation :', err);
      resolve(null);
    }
  });
}

// Rayon du flou boîte appliqué au masque BRUT avant seuillage (cf. paintPersonMask) —
// élimine le bruit "poivre et sel" du modèle (pixels isolés à confiance élevée loin
// de la personne, constaté en régie réelle : "des choses qui semblent apparaître loin
// du user filmé") : un pixel isolé à confiance 0.9 entouré de fond à 0.05 tombe, une
// fois moyenné sur un carré 3×3, largement sous EDGE_LOW ci-dessous — donc supprimé —
// alors qu'une VRAIE bordure (large de dizaines de pixels) n'en est presque pas
// affectée. Rayon 1 volontairement PETIT : un flou plus fort adoucirait aussi le
// contour réel, qu'EDGE_LOW/EDGE_HIGH doivent pouvoir re-durcir ensuite.
const NOISE_BLUR_RADIUS = 1;

// Reseuillage (smoothstep) du masque flouté : sous EDGE_LOW → transparent (supprime le
// bruit de fond résiduel après le flou), au-dessus d'EDGE_HIGH → opaque, transition
// douce entre les deux (pas un simple seuil dur, qui donnerait un contour "en
// escalier"). Non calibré sur un vrai flux — à resserrer (EDGE_HIGH-EDGE_LOW plus
// petit) si le contour reste encore imprécis, à élargir si des bouts de personne
// disparaissent (cheveux fins, etc.).
const EDGE_LOW  = 0.35;
const EDGE_HIGH = 0.65;

// Lissage temporel ASYMÉTRIQUE (par pixel) — RIEN ne lisse la MONTÉE (confiance qui
// augmente, une zone qui devient "personne") : elle s'applique instantanément à
// chaque frame. Seule la DESCENTE (une zone que la personne vient de quitter) est
// lissée, cf. TEMPORAL_SMOOTHING. Une première version symétrique (les deux sens
// lissés pareil) retardait aussi la montée — constaté en régie réelle : "quand je
// bouge, on voit un morceau du vrai arrière-plan puis le contour se réajuste" (le
// détourage n'avait pas encore rattrapé la personne, laissant un trou sur son bord
// d'attaque). Un peu de trainée résiduelle côté "personne s'en va" (l'inverse) est
// beaucoup moins gênant visuellement qu'un trou qui révèle le vrai fond. 1 = aucun
// lissage à la descente non plus, plus petit = descente plus lissée mais plus de
// trainée. Non calibré — à ajuster au test.
const TEMPORAL_SMOOTHING = 0.55;

// Masque lissé de la frame PRÉCÉDENTE, par canvas de sortie (cf. paintPersonMask) —
// une WeakMap plutôt qu'une propriété posée sur le canvas : plusieurs sources tournent
// en parallèle (cf. VideoEffectsPipelineManager, un maskCanvas par pipeline), chacune
// doit garder SON PROPRE historique, et une WeakMap se nettoie seule quand un pipeline
// (et son canvas) est détruit — pas de fuite mémoire à gérer à la main.
const prevMaskByCanvas = new WeakMap<HTMLCanvasElement, { data: Float32Array; width: number; height: number }>();

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/** Flou boîte séparable (horizontal puis vertical) — cf. NOISE_BLUR_RADIUS. */
function boxBlur(src: Float32Array, width: number, height: number, radius: number): Float32Array {
  const tmp = new Float32Array(src.length);
  const out = new Float32Array(src.length);
  for (let y = 0; y < height; y++) {
    const row = y * width;
    for (let x = 0; x < width; x++) {
      let sum = 0, count = 0;
      for (let dx = -radius; dx <= radius; dx++) {
        const xx = x + dx;
        if (xx < 0 || xx >= width) continue;
        sum += src[row + xx]; count++;
      }
      tmp[row + x] = sum / count;
    }
  }
  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) {
      let sum = 0, count = 0;
      for (let dy = -radius; dy <= radius; dy++) {
        const yy = y + dy;
        if (yy < 0 || yy >= height) continue;
        sum += tmp[yy * width + x]; count++;
      }
      out[y * width + x] = sum / count;
    }
  }
  return out;
}

/**
 * Construit (ou met à jour) un canvas dont le canal alpha porte la confiance "personne" —
 * blanc opaque = personne, transparent = fond. Redimensionné à `mask.width`×`mask.height`
 * (résolution du modèle, plus petite que la vidéo) : le lissage final vient du drawImage
 * qui l'étire ensuite à la taille du canvas de sortie (cf. drawPersonCutout dans
 * VideoEffect.ts) — mais ÇA ne nettoie pas le bruit du modèle lui-même, d'où le
 * flou+seuillage+lissage temporel ci-dessous (cf. NOISE_BLUR_RADIUS/EDGE_LOW/EDGE_HIGH/
 * TEMPORAL_SMOOTHING), ajoutés suite à un retour explicite ("parfois des défauts,
 * choses loin de la personne, détourage pas très régulier").
 */
export function paintPersonMask(mask: PersonMask, canvas: HTMLCanvasElement): void {
  if (canvas.width !== mask.width || canvas.height !== mask.height) {
    canvas.width  = mask.width;
    canvas.height = mask.height;
  }
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const blurred = boxBlur(mask.data, mask.width, mask.height, NOISE_BLUR_RADIUS);

  const prev = prevMaskByCanvas.get(canvas);
  let temporal: Float32Array;
  if (prev && prev.width === mask.width && prev.height === mask.height) {
    temporal = new Float32Array(blurred.length);
    for (let i = 0; i < blurred.length; i++) {
      // Montée (blurred > prev, une zone qui DEVIENT "personne") : instantanée, pas
      // de lissage — cf. commentaire de TEMPORAL_SMOOTHING. Seule la descente l'est.
      temporal[i] = blurred[i] >= prev.data[i]
        ? blurred[i]
        : prev.data[i] + (blurred[i] - prev.data[i]) * TEMPORAL_SMOOTHING;
    }
  } else {
    temporal = blurred; // première frame pour ce canvas — rien à lisser avec
  }
  prevMaskByCanvas.set(canvas, { data: temporal, width: mask.width, height: mask.height });

  const imageData = ctx.createImageData(mask.width, mask.height);
  const out = imageData.data;
  for (let i = 0; i < temporal.length; i++) {
    const alpha = Math.max(0, Math.min(255, Math.round(smoothstep(EDGE_LOW, EDGE_HIGH, temporal[i]) * 255)));
    const o = i * 4;
    out[o] = 255; out[o + 1] = 255; out[o + 2] = 255; out[o + 3] = alpha;
  }
  ctx.putImageData(imageData, 0, 0);
}
